/**
 * ChatAssistant — assistente de operações do shell.
 *
 * Gaveta lateral aberta pelo botão flutuante, presente em toda tela do
 * `AppShell`. Também roda embutido (`floating={false}`), que é como a página
 * de preview dos componentes de insights o monta.
 *
 * O markup é o do protótipo (`#directChatDrawer`). O conteúdo do painel usa a
 * família `chat-*` de `dashboardShell.css`, que já é o mesmo port — as bolhas,
 * o meta do turno, a conversa, o card de estado vazio e a lista de histórico
 * saem de lá. Só a gaveta em si tem classes próprias (`direct-chat-*`).
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import insightsService from '../../services/insightsService';
import chatLauncher from './chatLauncher';
import ChatMarkdown from '../common/ChatMarkdown';
import ptChatMessages from '../../i18n/locales/pt/chat.json';
import enChatMessages from '../../i18n/locales/en/chat.json';
import esChatMessages from '../../i18n/locales/es/chat.json';

/**
 * Wrapper around the shared ``ChatMarkdown`` renderer.
 *
 * The previous artisanal regex-based formatter only handled
 * ``**bold**``, ``*italic*``, `` `code` ``, bullets and numbered lists.
 * Anything else the LLM emitted (notably ``#``/``##``/``###`` headings,
 * ``>`` quotes, links, ``---`` rules, and tables) leaked through as raw
 * ``#`` characters and looked broken to users (#2404). We now delegate
 * to ``react-markdown`` + ``remark-gfm`` via ``ChatMarkdown`` so the
 * assistant gets the same bubble-friendly typography as the public
 * agent chat.
 */
const FormattedMessage = ({ content }) => <ChatMarkdown content={content} />;

const chatFallbacks = {
  pt: ptChatMessages.chat,
  en: enChatMessages.chat,
  es: esChatMessages.chat
};

/** Ordem das capacidades no card de estado vazio, igual à do protótipo. */
const EMPTY_STATE_ITEMS = ['executions', 'executors', 'schedules', 'automations'];

/** O badge do histórico para de contar em 9, como o `max` que ele tinha. */
const HISTORY_BADGE_MAX = 9;

const resolveBaseLanguage = (language) => String(language || 'pt').split('-')[0].toLowerCase();

const getNestedValue = (source, key) => (
  key
    .split('.')
    .reduce((current, part) => (current && current[part] !== undefined ? current[part] : undefined), source)
);

const interpolateFallback = (template, options = {}) => (
  Object.entries(options).reduce(
    (message, [token, value]) => message.replaceAll(`{{${token}}}`, value ?? ''),
    template
  )
);

const ChatAssistant = ({
  floating = false,
  contextType = 'general',
  height = 500,
  onClose,
  initialContext = null,
  onContextCleared
}) => {
  const { t, i18n, ready } = useTranslation();
  const [open, setOpen] = useState(!floating);
  // Context handed over by another screen (an insight card) through the
  // launcher. The prop still wins when a caller renders its own instance.
  const [launchedContext, setLaunchedContext] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  // O protótipo escreve `directChatDrawer`, `directChatInput` etc. à mão
  // porque é HTML estático com uma instância só. Aqui o componente é montado
  // mais de uma vez na mesma página — a rota de preview monta o modo embutido
  // e o flutuante juntos — e ids fixos se duplicariam: o `htmlFor` do segundo
  // composer apontaria para o `textarea` do primeiro, e o `aria-labelledby`
  // do diálogo para o título do outro. `useId` é o que o repo já usa nesse
  // caso (LanguageSwitcher, UserSearchField, IdpClassesMultiselect).
  const uid = useId();
  const drawerId = `direct-chat-drawer-${uid}`;
  const titleId = `direct-chat-title-${uid}`;
  const inputId = `direct-chat-input-${uid}`;
  const locale = i18n.resolvedLanguage || i18n.language || undefined;
  const translateLabel = useCallback((key, options = {}) => {
    if (ready && i18n.isInitialized && i18n.exists(key)) {
      return t(key, options);
    }

    const baseLanguage = resolveBaseLanguage(i18n.resolvedLanguage || i18n.language);
    const fallbackValue =
      getNestedValue(chatFallbacks[baseLanguage], key.replace(/^chat\./, '')) ??
      getNestedValue(chatFallbacks.en, key.replace(/^chat\./, ''));

    if (typeof fallbackValue === 'string') {
      return interpolateFallback(fallbackValue, options);
    }

    return key;
  }, [i18n, ready, t]);

  const quickActions = [
    {
      label: translateLabel('chat.quickActions.recentFailures.label'),
      query: translateLabel('chat.quickActions.recentFailures.query')
    },
    {
      label: translateLabel('chat.quickActions.executors.label'),
      query: translateLabel('chat.quickActions.executors.query')
    },
    {
      label: translateLabel('chat.quickActions.schedules.label'),
      query: translateLabel('chat.quickActions.schedules.query')
    }
  ];

  const normalizeText = (value) => (
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  );

  const formatSessionDate = (value) => (
    new Intl.DateTimeFormat(locale).format(new Date(value))
  );

  useEffect(() => {
    if (!floating) return undefined;
    return chatLauncher.register(({ context }) => {
      setLaunchedContext(context);
      setOpen(true);
    });
  }, [floating]);

  useEffect(() => {
    if (floating && open) {
      document.body.classList.add('insight-chat-open');
    } else if (floating) {
      document.body.classList.remove('insight-chat-open');
    }

    return () => {
      if (floating) {
        document.body.classList.remove('insight-chat-open');
      }
    };
  }, [floating, open]);

  const activeContext = initialContext || launchedContext;

  useEffect(() => {
    if (!activeContext) {
      return undefined;
    }

    setOpen(true);

    const contextMessage = translateLabel('chat.contextMessage', {
      title: activeContext.title,
      description: activeContext.description
    });

    setMessages([
      {
        role: 'assistant',
        content: translateLabel('chat.welcome')
      },
      {
        role: 'user',
        content: contextMessage
      }
    ]);
    setSessionId(null);

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await insightsService.sendMessage(contextMessage, null, 'alert');
        setSessionId(response.session_id);
        setMessages((prev) => [...prev, { role: 'assistant', content: response.message }]);
      } catch (error) {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: translateLabel('chat.mockResponses.general')
        }]);
      } finally {
        setLoading(false);
      }
    }, 100);

    if (onContextCleared) {
      onContextCleared();
    }

    return () => clearTimeout(timer);
  }, [activeContext, onContextCleared, translateLabel]);

  useEffect(() => {
    if (!open || activeContext) {
      return undefined;
    }

    loadSessions();

    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [activeContext, open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const data = await insightsService.getChatSessions(10);
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadSession = async (session) => {
    try {
      const data = await insightsService.getSessionMessages(session.session_id);
      setMessages(data.messages || []);
      setSessionId(session.session_id);
      setShowHistory(false);
    } catch (error) {
      console.error('Failed to load session:', error);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  const getMockResponse = (question) => {
    const normalizedQuestion = normalizeText(question);

    const hasFailureIntent = [
      'falha', 'falhas', 'erro', 'erros', 'problema', 'problemas',
      'failure', 'failures', 'problem', 'problems', 'falla', 'fallas'
    ].some((term) => normalizedQuestion.includes(term));

    if (hasFailureIntent) {
      return translateLabel('chat.mockResponses.failures');
    }

    // `agente` saiu daqui (PBI 4518). No produto de hoje agente é o agente de
    // IA, uma entidade própria com página própria; executor é infraestrutura
    // interna. Enquanto os dois dividiam a mesma lista, perguntar "quais são
    // meus agentes" respondia sobre executor — que é justamente a confusão de
    // vocabulário que a PBI veio desfazer.
    const hasExecutorIntent = [
      'executor', 'executors', 'ejecutor', 'ejecutores'
    ].some((term) => normalizedQuestion.includes(term));

    if (hasExecutorIntent) {
      return translateLabel('chat.mockResponses.executors');
    }

    const hasScheduleIntent = [
      'agendamento', 'agendamentos', 'schedule', 'schedules',
      'programacion', 'programaciones', 'cronograma', 'cronogramas'
    ].some((term) => normalizedQuestion.includes(term));

    if (hasScheduleIntent) {
      return translateLabel('chat.mockResponses.schedules');
    }

    return translateLabel('chat.mockResponses.general');
  };

  const handleSend = async () => {
    if (!input.trim() || loading) {
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await insightsService.sendMessage(userMessage, sessionId, contextType);
      setSessionId(response.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: response.message }]);
    } catch (error) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: getMockResponse(userMessage)
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = useCallback(() => {
    setLaunchedContext(null);
    setOpen(false);
    // O `Drawer` do MUI devolvia o foco ao gatilho sozinho. Sem isso, fechar
    // pelo botão do cabeçalho deixaria o foco dentro de uma subárvore que
    // acabou de virar `aria-hidden` e `visibility: hidden`, e o teclado
    // recomeçaria do topo do documento.
    fabRef.current?.focus();
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // O `Drawer` do MUI fechava no `Escape` sozinho. Com markup próprio o
  // atalho volta à mão, como o protótipo faz e como o modal de detalhe do
  // insight já faz aqui.
  useEffect(() => {
    if (!floating || !open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [floating, open, handleClose]);

  const applyQuickAction = (query) => {
    setInput(query);
    inputRef.current?.focus();
  };

  const historyCount = Math.min(sessions.length, HISTORY_BADGE_MAX);

  const emptyState = (
    <div className="direct-chat-empty" hidden={messages.length > 0}>
      <div className="chat-empty-card">
        <p className="chat-empty-text">{translateLabel('chat.emptyState.intro')}</p>
        <ul className="direct-chat-empty-list">
          {EMPTY_STATE_ITEMS.map((item) => (
            <li key={item}>{translateLabel(`chat.emptyState.items.${item}`)}</li>
          ))}
        </ul>
        <p className="chat-empty-text">{translateLabel('chat.emptyState.outro')}</p>
      </div>
    </div>
  );

  const conversation = (
    <>
      {emptyState}
      <div className="chat-thread" aria-live="polite">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`chat-message ${message.role === 'user' ? 'user' : 'agent'}`}
          >
            <span className="chat-meta">
              {translateLabel(message.role === 'user' ? 'chat.roleUser' : 'chat.roleAssistant')}
            </span>
            <div className="chat-bubble">
              {message.role === 'assistant'
                ? <FormattedMessage content={message.content} />
                : message.content}
            </div>
          </div>
        ))}

        {loading && (
          <span className="chat-meta direct-chat-thinking">{translateLabel('chat.thinking')}</span>
        )}

        <div ref={messagesEndRef} />
      </div>
    </>
  );

  const history = (
    <div className="chat-history">
      {sessions.length === 0 ? (
        <p className="chat-empty-hint">{translateLabel('chat.noHistory')}</p>
      ) : (
        sessions.map((session) => (
          <button
            key={session.session_id}
            type="button"
            className="chat-history-item"
            onClick={() => loadSession(session)}
          >
            <span className="chat-history-title-row">
              <span className="chat-history-title">
                {session.title || translateLabel('chat.untitled')}
              </span>
            </span>
            <span className="chat-history-date">{formatSessionDate(session.updated_at)}</span>
          </button>
        ))
      )}
    </div>
  );

  const panel = (
    <>
      <div className="direct-chat-header">
        <div className="direct-chat-brand">
          <div className="direct-chat-brand-icon" aria-hidden="true">
            <span className="material-symbols-rounded">smart_toy</span>
          </div>
          <div>
            <h3 id={titleId}>{translateLabel('chat.title')}</h3>
          </div>
        </div>
        <div className="direct-chat-header-actions">
          <button
            type="button"
            className="direct-chat-header-btn"
            aria-label={translateLabel('chat.newChat')}
            onClick={startNewSession}
          >
            <span className="material-symbols-rounded" aria-hidden="true">add</span>
          </button>
          <button
            type="button"
            className="direct-chat-header-btn"
            aria-label={translateLabel('chat.history')}
            aria-expanded={showHistory}
            onClick={() => setShowHistory((previous) => !previous)}
          >
            <span className="material-symbols-rounded" aria-hidden="true">history</span>
            {historyCount > 0 && (
              <span className="direct-chat-header-badge" aria-hidden="true">{historyCount}</span>
            )}
          </button>
          {floating && (
            <button
              type="button"
              className="direct-chat-header-btn"
              aria-label={t('common.close')}
              onClick={handleClose}
            >
              <span className="material-symbols-rounded" aria-hidden="true">close</span>
            </button>
          )}
        </div>
      </div>

      <div className="direct-chat-subbar">
        <p className="chat-subtitle">{translateLabel('chat.subtitle')}</p>
      </div>

      <div className="direct-chat-body">
        {showHistory ? history : conversation}
      </div>

      <div className="direct-chat-suggestions">
        {quickActions.map((action) => (
          <button
            key={action.label}
            type="button"
            className="direct-chat-chip"
            onClick={() => applyQuickAction(action.query)}
          >
            {action.label}
          </button>
        ))}
      </div>

      <div className="direct-chat-composer">
        <label className="direct-chat-sr-only" htmlFor={inputId}>
          {translateLabel('chat.placeholder')}
        </label>
        <div className="direct-chat-input-shell">
          <textarea
            id={inputId}
            ref={inputRef}
            className="direct-chat-input"
            rows={2}
            maxLength={32000}
            autoComplete="off"
            placeholder={translateLabel('chat.placeholder')}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <button
            type="button"
            className="direct-chat-send"
            aria-label={translateLabel('chat.send')}
            onClick={handleSend}
            disabled={!input.trim() || loading}
          >
            <span className="material-symbols-rounded" aria-hidden="true">send</span>
          </button>
        </div>
      </div>
    </>
  );

  if (!floating) {
    return (
      <div className="direct-chat-embedded" style={{ height }}>
        {panel}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="fab has-tooltip"
        ref={fabRef}
        data-tooltip={translateLabel('chat.open')}
        aria-label={translateLabel('chat.open')}
        aria-haspopup="dialog"
        aria-controls={drawerId}
        onClick={handleOpen}
      >
        <span className="material-symbols-rounded" aria-hidden="true">chat</span>
      </button>

      <div
        id={drawerId}
        className={`direct-chat-drawer${open ? ' open' : ''}`}
        aria-hidden={!open}
      >
        {/* O véu fecha ao clique, como no protótipo. Leva `chat.close`
            ("Fechar chat") em vez de `common.close` ("Fechar") para não ter o
            mesmo nome acessível que o botão de fechar do cabeçalho. */}
        <button
          type="button"
          className="direct-chat-backdrop"
          aria-label={translateLabel('chat.close')}
          tabIndex={-1}
          onClick={handleClose}
        />
        <aside
          className="direct-chat-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          {panel}
        </aside>
      </div>
    </>
  );
};

export default ChatAssistant;
